import { createContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [adminValid, setAdminValid] = useState(false); // ← これだけで管理
  const [popupMessage, setPopupMessage] = useState("");

  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("access_token");
    // 以前は login が返さない `role` を読んでいたため adminValid が常に false だった。
    // 管理者判定はサーバの ADMIN_EMAILS が正で、login レスポンスの is_admin がその結果。
    const storedIsAdmin = localStorage.getItem("is_admin");

    if (token && storedIsAdmin === null) {
      // is_admin を持たない＝この仕組みを入れる前に発行されたセッション。
      // 管理者かどうかを判定できず、管理者が管理画面から締め出されるため、
      // 一度だけ再ログインしてもらう。
      localStorage.removeItem("access_token");
      localStorage.removeItem("user_id");
      localStorage.removeItem("role");
      setIsAuthenticated(false);
      return;
    }

    if (token) {
      setIsAuthenticated(true);
      setAdminValid(storedIsAdmin === "true");
    } else {
      setIsAuthenticated(false);
    }
  }, []);

  const login = async (email, password) => {
    try {
      // const response = await fetch("http://127.0.0.1:8000/api/auth/login", {
      const response = await fetch("/api/auth/login", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        throw new Error("ログインに失敗しました");
      }

      const data = await response.json();

      localStorage.setItem("access_token", data.access_token);
      localStorage.setItem("user_id", data.user_id);
      localStorage.setItem("is_admin", data.is_admin ? "true" : "false");
      // 旧キー。古い端末に残ったままだと紛らわしいので消しておく
      localStorage.removeItem("role");

      setIsAuthenticated(true);
      setAdminValid(Boolean(data.is_admin));

      navigate("/");
      return true;
    } catch (error) {
      console.error("ログインエラー:", error);
      return false;
    }
  };

  const logout = async () => {
    // localStorage を消すだけではトークンは有効期限（7日）まで通り続けるため、
    // サーバ側でも失効させる。失敗しても手元のログアウトは必ず完了させる。
    const token = localStorage.getItem("access_token");
    if (token) {
      try {
        await fetch("/api/auth/logout", {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch (error) {
        console.error("ログアウトエラー:", error);
      }
    }

    localStorage.removeItem("access_token");
    localStorage.removeItem("user_id");
    localStorage.removeItem("is_admin");
    localStorage.removeItem("role");

    setIsAuthenticated(false);
    setAdminValid(false);
    navigate("/login");
  };

  // 管理者が発行した仮パスワードを本人が置き換えるための導線。
  // サーバは変更と同時に全端末のトークンを失効させ、この端末ぶんだけ再発行して返す。
  const changePassword = async (currentPassword, newPassword) => {
    const token = localStorage.getItem("access_token");
    const response = await fetch("/api/auth/change_password", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        current_password: currentPassword,
        new_password: newPassword,
      }),
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.detail || "パスワードの変更に失敗しました");
    }

    // 受け取った新しいトークンに差し替えないと、次のリクエストで 401 になる
    const data = await response.json();
    localStorage.setItem("access_token", data.access_token);
    localStorage.setItem("is_admin", data.is_admin ? "true" : "false");
    setAdminValid(Boolean(data.is_admin));
  };

  const handleNavigation = (path, isValid, navigate) => {
    if (!isValid) {
    navigate(path)
    } else {
      navigate(path);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        login,
        logout,
        changePassword,
        adminValid,
        handleNavigation,
        popupMessage,
        setPopupMessage,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
